import { useMemo } from "react";

/**
 * Decorative animated background for the maintenance page.
 *
 * Cycles through the 13 SVG icons in /public/assets/maintenance_page/, repeating
 * them so the canvas feels populated. Each instance gets a deterministic-but-
 * varied position, size, drift direction, duration, delay and base rotation.
 *
 * Pointer-events: none, blends multiply (light) / screen (dark) — see SCSS.
 * Respects prefers-reduced-motion via SCSS.
 */
const SVG_COUNT = 13;        // unique source files (1.svg … 13.svg)
const ICON_COUNT = 32;       // total icons rendered (cycles through the 13 sources)
const VARIANTS = ["v7-drift-a", "v7-drift-b", "v7-drift-c", "v7-drift-d"];

/**
 * Spread points on a unit square using a low-discrepancy (golden-ratio) sequence
 * so icons feel evenly distributed without forming an obvious grid.
 */
function buildAnchors(count) {
    const phi = 0.6180339887498949;
    const phi2 = 0.4501836112948735; // sqrt(2) fractional, good 2nd dim
    const out = [];
    for (let i = 0; i < count; i++) {
        const x = ((i + 1) * phi) % 1;
        const y = ((i + 1) * phi2) % 1;
        // Keep a 3% margin so icons don't get clipped at the edges
        out.push({
            left: `${(x * 94 + 3).toFixed(2)}%`,
            top: `${(y * 94 + 3).toFixed(2)}%`,
        });
    }
    return out;
}

export default function FloatingMaintenanceBg() {
    const items = useMemo(() => {
        const anchors = buildAnchors(ICON_COUNT);
        return Array.from({ length: ICON_COUNT }, (_, i) => {
            const n = (i % SVG_COUNT) + 1;       // cycle through the 13 sources
            const pos = anchors[i];
            const variant = VARIANTS[i % VARIANTS.length];
            // Deterministic but varied values per icon
            const dur = 14 + ((i * 17) % 26);             // 14..40s — faster overall
            const delay = -((i * 31) % 22);                // negative → desync starts
            const size = 30 + ((i * 7) % 38);              // 30..68px — smaller, denser
            const baseRotate = ((i * 47) % 360) - 180;     // -180..180
            return { id: i, n, pos, variant, dur, delay, size, baseRotate };
        });
    }, []);

    return (
        <div className="v7-floating-bg" aria-hidden="true">
            {items.map(({ id, n, pos, variant, dur, delay, size, baseRotate }) => (
                <img
                    key={id}
                    src={`/assets/maintenance_page/${n}.svg`}
                    alt=""
                    className={`v7-floating-icon ${variant}`}
                    style={{
                        opacity: 0.3,
                        top: pos.top,
                        left: pos.left,
                        width: `${size}px`,
                        height: `${size}px`,
                        animationDuration: `${dur}s`,
                        animationDelay: `${delay}s`,
                        // CSS variable consumed by the keyframes for a varied resting rotation
                        "--v7-base-rot": `${baseRotate}deg`,
                    }}
                    loading="lazy"
                    draggable={false}
                />
            ))}
        </div>
    );
}
